import React, {
  FunctionComponent,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import {
  makeStyles,
  Theme,
  createStyles,
  IconButton,
  Button,
  Typography,
} from "@material-ui/core";
import { ArrowBack, ArrowForward } from "@material-ui/icons";
import { useSelector, useDispatch, batch } from "react-redux";

import { GroupListBox } from "./components/GroupListBox";
import { ProcessListBox } from "./components/ProcessListBox";
import { RootState } from "../../../store/reducers";
import { appTheme } from "../../../styling/style";
import { FoodItem } from "../../../data/models/documentProperties/foodItem";
import { cachedDocumentSelector } from "../../../store/data/selectors/documentCache";
import { Document } from "../../../data/models/document";
import { updateRetentionFactor } from "../../../store/data/current-document/action-creators/foodItems";
import { FoodId } from "../../../data/models/documentProperties/foodId";
import { foodItemsSelector } from "../../../store/data/current-document/selectors/foodItems";
import { FoodworksTooltip } from "../../common/InfoTooltip";
import { BaseDialog } from "../BaseDialog";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleBar: {
      display: "flex",
      flex: 1,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    titleBarButtons: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    typography: {
      color: appTheme.colors.xiketic,
    },
    selectButton: {
      background: appTheme.colors.primary,
      marginRight: 40,
    },
    listBoxesCasing: {
      marginTop: 5,
      display: "flex",
      flexDirection: "row",
    },
    description: {
      marginTop: 10,
    },
  })
);

interface RetentionProfileData {
  groupId: string | null;
  profileId: string | null;
}

interface RetentionProfileDataMap {
  [key: string]: RetentionProfileData;
}

interface RetentionFactorUpdateData {
  rowIndex: number;
  id: string | null;
}

export interface RetentionFactorDialogProps {
  dayIndex: number;
  sectionIndex: number;
  currentFoodItemIndex: number;
  open: boolean;
  onClose: () => void;
}

interface RetentionFactorDialogOption {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  color?: "secondary" | "default" | "inherit" | "primary" | undefined;
}

export const RetentionFactorDialog: FunctionComponent<RetentionFactorDialogProps> = ({
  dayIndex,
  sectionIndex,
  currentFoodItemIndex,
  open,
  onClose,
}) => {
  const classes = useStyles();

  /* *** Actions *** */

  const dispatch = useDispatch();

  const onUpdateRetentionFactor = (rowIndex: number, id: string | null) => {
    dispatch(updateRetentionFactor(dayIndex, sectionIndex, rowIndex, id));
  };

  /* ----- */

  /* *** State *** */

  const selectFoodItems = useMemo(foodItemsSelector, []);

  const foodItems: FoodItem[] = useSelector<RootState, FoodItem[]>(
    (state) => selectFoodItems(state, dayIndex, sectionIndex).items
  );

  const [foodItemIndex, setFoodItemIndex] = useState(currentFoodItemIndex);
  const [searchInput, setSearchInput] = useState("");

  const currentFoodItemDocument: Document | undefined = useSelector<
    RootState,
    Document | undefined
  >((state) => {
    if (!foodItems[foodItemIndex]?.foodId) return undefined;

    return cachedDocumentSelector(
      state,
      new FoodId(foodItems[foodItemIndex].foodId!).identifier
    );
  });

  const setInitialProfiles = useCallback(() => {
    const initialProfiles: RetentionProfileDataMap = {};
    foodItems.forEach((foodItem: FoodItem, index: number): void => {
      const retentionProfilePair: RetentionProfileData = foodItem.retentionFactor
        ? {
            groupId: foodItem.retentionFactor.groupId,
            profileId: foodItem.retentionFactor.profileId,
          }
        : { groupId: null, profileId: null };
      initialProfiles[index] = retentionProfilePair;
    });
    return initialProfiles;
  }, [foodItems]);

  const [selectedProfiles, setSelectedProfiles] = useState(
    setInitialProfiles()
  );

  /* *** ----- *** */

  const handleSelectGroup = (selectedGroupId: string) => {
    setSelectedProfiles({
      ...selectedProfiles,
      [foodItemIndex]: {
        groupId:
          selectedGroupId === selectedProfiles[foodItemIndex].groupId
            ? null
            : selectedGroupId,
        profileId: null,
      },
    });
  };

  const handleSelectProcess = (selectedProcess: string) => {
    setSelectedProfiles({
      ...selectedProfiles,
      [foodItemIndex]: {
        ...selectedProfiles[foodItemIndex],
        profileId:
          selectedProcess === selectedProfiles[foodItemIndex].profileId
            ? null
            : selectedProcess,
      },
    });
  };

  useEffect(() => {
    setSelectedProfiles(setInitialProfiles());
  }, [foodItems, setInitialProfiles]);

  const getUnsavedRetentionFactors = (): RetentionFactorUpdateData[] => {
    const retentionFactorsToUpdate: RetentionFactorUpdateData[] = [];

    for (const [rowIndex, data] of Object.entries(selectedProfiles)) {
      const isNotChanged: boolean =
        !(
          data.profileId ||
          foodItems[Number(rowIndex)].retentionFactor?.profileId
        ) ||
        data.profileId ===
          foodItems[Number(rowIndex)].retentionFactor?.profileId;

      const isNotValid: boolean = Boolean(data.groupId) && !data.profileId;
      if (isNotChanged || isNotValid) continue;

      retentionFactorsToUpdate.push({
        rowIndex: Number(rowIndex),
        id: data.profileId || null,
      });
    }
    return retentionFactorsToUpdate;
  };

  const handleOnSave = () => {
    const retentionFactorsToUpdate: RetentionFactorUpdateData[] = getUnsavedRetentionFactors();

    batch(() => {
      for (const factor of retentionFactorsToUpdate) {
        onUpdateRetentionFactor(factor.rowIndex, factor.id);
      }
    });
  };

  const handleOnSaveAndExit = () => {
    handleOnSave();
    onClose();
  };

  const handleOnClear = () => {
    setSelectedProfiles({
      ...selectedProfiles,
      [foodItemIndex]: { groupId: null, profileId: null },
    });
  };

  const changeFoodItem = (index: number): void => setFoodItemIndex(index);

  const nextFoodItem = (index: number): void => {
    if (index > foodItems.length - 1) index = 0;

    if (foodItems[index].foodId?.datasourceId) {
      changeFoodItem(index);
      return;
    }
    nextFoodItem(++index);
  };

  const previousFoodItem = (index: number): void => {
    if (index < 0) index = foodItems.length - 1;

    if (foodItems[index].foodId?.datasourceId) {
      changeFoodItem(index);
      return;
    }
    previousFoodItem(--index);
  };

  const hasUnsavedChanges = (): boolean =>
    Boolean(getUnsavedRetentionFactors().length);
  const noRetentionGroupSelected = (): boolean =>
    !selectedProfiles[foodItemIndex]?.groupId;

  const selectedGroupId = (): string | null =>
    selectedProfiles[foodItemIndex]?.groupId || null;
  const selectedProcessId = (): string | null =>
    selectedProfiles[foodItemIndex]?.profileId || null;

  const onNextClick = (index: number): void => {
    nextFoodItem(index);
    setSearchInput("");
  };

  const onPreviousClick = (index: number): void => {
    previousFoodItem(index);
    setSearchInput("");
  };

  const foodItemDialogActions: ReactNode = (
    <div className={classes.titleBarButtons}>
      <FoodworksTooltip title="Previous ingredient">
        <span>
          <IconButton
            size="small"
            onClick={() => onPreviousClick(foodItemIndex - 1)}
            disabled={foodItems.length === 1}
            color="secondary"
          >
            <ArrowBack />
          </IconButton>
        </span>
      </FoodworksTooltip>

      <FoodworksTooltip title="Next ingredient">
        <span>
          <IconButton
            size="medium"
            onClick={() => onNextClick(foodItemIndex + 1)}
            disabled={foodItems.length === 1}
            color="secondary"
          >
            <ArrowForward />
          </IconButton>
        </span>
      </FoodworksTooltip>
    </div>
  );

  const getApplyChangesText = (text: string): string => {
    const unsavedChangesAmount: number = getUnsavedRetentionFactors().length;
    switch (unsavedChangesAmount) {
      case 0:
        return `${text} changes`;
      case 1:
        return `${text} (1 change)`;
      default:
        return `${text} (${unsavedChangesAmount} changes)`;
    }
  };

  const bottomDialogActions: RetentionFactorDialogOption[] = [
    {
      text: "Cancel",
      onClick: onClose,
      disabled: false,
      color: "default",
    },
    {
      text: "Clear",
      onClick: () => handleOnClear(),
      disabled: noRetentionGroupSelected(),
      color: "secondary",
    },
    {
      text: getApplyChangesText("Apply"),
      onClick: () => handleOnSave(),
      disabled: !hasUnsavedChanges(),
      color: "secondary",
    },
    {
      text: getApplyChangesText("OK"),
      onClick: () => handleOnSaveAndExit(),
      disabled: !hasUnsavedChanges(),
      color: "secondary",
    },
  ];

  const bottomActionButtons: ReactNode[] = bottomDialogActions.map(
    (option: RetentionFactorDialogOption) => (
      <Button
        key={option.text}
        onClick={option.onClick}
        disabled={option.disabled}
        color={option.color}
      >
        {option.text}
      </Button>
    )
  );

  const dialogBody: ReactNode = (
    <>
    <Typography>Setting retention factors ensures that the nutritional analysis for the recipe reflects the vitamin and mineral changes that occur during the cooking process.
<br/>
Set a retention factor for {currentFoodItemDocument?.name}</Typography>
      {foodItemDialogActions}
      <div className={classes.listBoxesCasing}>
        <GroupListBox
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          currentSelectedGroupId={selectedGroupId()}
          onSelectGroup={handleSelectGroup}
        />
        <ProcessListBox
          selectedGroupId={selectedGroupId()}
          currentSelectedProfileId={selectedProcessId()}
          onSelectProcess={handleSelectProcess}
        />
      </div>
    </>
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={currentFoodItemDocument?.name || ""}
      body={dialogBody}
      action={bottomActionButtons}
      maxWidth="xl"
    ></BaseDialog>
  );
};
